import { Warning } from "@phosphor-icons/react"
import { type CustomTooltipProps } from "ag-grid-react"
import { useEffect, useRef } from "react"

import { cn } from "~/utils/helpers"
import HeaderTooltip from "./HeaderTooltip"
import TooltipMessageCard from "./TooltipMessageCard"

export type LineWarningsTooltipValue = {
  tooltipType: "LineWarnings"
  warnings: {
    title: string
    tone: "warning" | "notice"
    count: number
    text?: string
    action: string
  }[]
}

/**
 * The warning column's hover: each reason with its icon, colour and count, the orange ones first.
 * Not interactive: AG Grid keeps a tooltip open while the pointer is over it, so the tooltip's
 * host element is made pointer-transparent and the tooltip goes away when the pointer leaves the cell.
 */
export default function LineWarningsTooltip(props: CustomTooltipProps) {
  const anchor = useRef<HTMLDivElement>(null)
  useEffect(() => {
    // Both the tooltip's host and the popup around it, or the popup still catches the pointer.
    for (const selector of [".ag-tooltip-custom", ".ag-popup"]) {
      const host = anchor.current?.closest<HTMLElement>(selector)
      if (host) host.style.pointerEvents = "none"
    }
  })
  if (props.location === "header") return <HeaderTooltip {...props} />
  const value = props.value as LineWarningsTooltipValue | undefined
  if (!value || value.warnings.length === 0) return null
  return (
    <div ref={anchor} className="pointer-events-none">
      <TooltipMessageCard className="flex flex-col divide-y divide-border-base whitespace-normal">
        {value.warnings.map((warning) => (
          <div key={warning.title} className="flex flex-col gap-0.5 py-2.5 first:pt-0 last:pb-0">
            <span className="flex items-center gap-1.5 font-semibold text-fg-base">
              <Warning
                size={14}
                weight="fill"
                className={cn(
                  "shrink-0",
                  warning.tone === "warning" ? "text-fg-warning-icon" : "text-fg-muted",
                )}
              />
              {warning.title}
              {warning.text == null || warning.count > 1 ? (
                <span className="font-normal text-fg-muted">{`· ${warning.count}`}</span>
              ) : null}
            </span>
            {warning.text != null && <span className="text-fg-subtle">{warning.text}</span>}
            <span className="text-fg-base">{warning.action}</span>
          </div>
        ))}
      </TooltipMessageCard>
    </div>
  )
}
